import {
  Paper,
  Typography,
  TextField,
  Grid,
  Switch,
  FormControlLabel,
  Toolbar,
  Button,
  IconButton,
  Collapse,
  Snackbar,
  Box,
  useMediaQuery,
  Theme,
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import CloseIcon from '@material-ui/icons/Close';
import * as React from 'react';
import {
  DelayedLinearProgress,
  ResponsiveIconButton,
  SortableTable,
  ConfirmDialog,
} from 'components';
import { SortableTableHeader } from 'components/SortableTable/components';
import { ExperienceService } from 'services';
import { Experience } from 'interfaces';

/**
 * Inline experiences editor. No Dialog: the add / edit form expands
 * directly under the toolbar as a collapsible panel. Same flow as
 * SpecialDaysTab redesign so we avoid the periodic-remount bug we saw
 * when FormDialog was used inside the nested Settings sub-tabs.
 */

const columns: SortableTableHeader[] = [
  { key: 'banner', label: 'Banner' },
  { key: 'title', label: 'Title' },
  { key: 'experience_at', label: 'Date / Time' },
  { key: 'active', label: 'Status' },
  { key: 'controls', sortable: false },
];

interface FormState {
  id?: number;
  title: string;
  description: string;
  experience_at: string;
  is_active: boolean;
  is_fully_booked: boolean;
  banner_url?: string | null;
  file?: File | null;
}

const blankForm = (): FormState => ({
  title: '',
  description: '',
  experience_at: '',
  is_active: true,
  is_fully_booked: false,
  banner_url: null,
  file: null,
});

const bannerSrc = (key?: string | null) =>
  key ? `https://api.pekingltd.com/experiences/${key}` : null;

export const ExperiencesTab: React.FC = () => {
  const [loading, setLoading] = React.useState(true);
  const [rows, setRows] = React.useState<Experience[]>([]);
  const [panelOpen, setPanelOpen] = React.useState(false);
  const [form, setForm] = React.useState<FormState>(blankForm());
  const [submitting, setSubmitting] = React.useState(false);
  const [deleting, setDeleting] = React.useState<Experience | null>(null);
  const [snackbar, setSnackbar] = React.useState<string | null>(null);
  const isMobile = useMediaQuery((t: Theme) => t.breakpoints.down('sm'));

  const load = React.useCallback(() => {
    setLoading(true);
    ExperienceService.getAll()
      .then((data) => setRows(data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setForm(blankForm());
    setPanelOpen(true);
  };

  const openEdit = (row: Experience) => {
    const dt = new Date(row.experienceAt);
    const pad = (n: number) => String(n).padStart(2, '0');
    setForm({
      id: row.id,
      title: row.title,
      description: row.description || '',
      experience_at: `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(
        dt.getDate(),
      )}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`,
      is_active: !!row.isActive,
      is_fully_booked: !!row.isFullyBooked,
      banner_url: row.bannerUrl || null,
      file: null,
    });
    setPanelOpen(true);
  };

  const handleSave = async () => {
    if (!form.title || !form.experience_at) {
      setSnackbar('Title and Date/Time are required');
      return;
    }
    setSubmitting(true);
    // Text fields go via PUT (URLSearchParams) or POST (FormData). Banner
    // ALWAYS goes via the dedicated POST /experiences/{id}/banner endpoint
    // so the PUT-can't-carry-files limitation never bites.
    const payload: any = {
      title: form.title,
      description: form.description || null,
      experience_at: form.experience_at.replace('T', ' ') + ':00',
      is_active: form.is_active ? 1 : 0,
      is_fully_booked: form.is_fully_booked ? 1 : 0,
    };

    try {
      let savedId: number | undefined = form.id;
      if (form.id) {
        await ExperienceService.update(form.id, payload);
      } else {
        const created: any = await ExperienceService.create(payload);
        savedId = created && created.id;
      }
      if (form.file && savedId) {
        await ExperienceService.updateBanner(savedId, form.file);
      }
      setPanelOpen(false);
      setForm(blankForm());
      setSnackbar('Saved');
      load();
    } catch (e) {
      setSnackbar((e as Error)?.message || 'Failed to save');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (row: Experience) => {
    try {
      await ExperienceService.remove(row.id);
      setDeleting(null);
      load();
    } catch (e) {
      setSnackbar((e as Error)?.message || 'Failed to delete');
    }
  };

  const tableRows = rows.map((r) => {
    const src = bannerSrc(r.bannerUrl);
    const dt = new Date(r.experienceAt);
    return {
      key: String(r.id),
      cells: [
        {
          key: 'banner',
          display: src ? (
            <img
              src={src}
              alt=""
              style={{
                width: 80,
                height: 50,
                objectFit: 'cover',
                borderRadius: 4,
              }}
            />
          ) : (
            <span style={{ color: '#aaa', fontSize: '0.85rem' }}>none</span>
          ),
        },
        { key: 'title', display: r.title },
        {
          key: 'experience_at',
          display: isNaN(dt.getTime()) ? '—' : dt.toLocaleString(),
        },
        {
          key: 'active',
          display: (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {r.isActive ? (
                <span
                  style={{
                    background: '#E8F5E9',
                    color: '#2E7D32',
                    padding: '2px 8px',
                    borderRadius: 4,
                    fontSize: '0.78rem',
                    fontWeight: 600,
                  }}
                >
                  Active
                </span>
              ) : (
                <span
                  style={{
                    background: '#EEE',
                    color: '#888',
                    padding: '2px 8px',
                    borderRadius: 4,
                    fontSize: '0.78rem',
                    fontWeight: 600,
                  }}
                >
                  Hidden
                </span>
              )}
              {r.isFullyBooked && (
                <span
                  style={{
                    background: '#FBE4E5',
                    color: '#C5252E',
                    padding: '2px 8px',
                    borderRadius: 4,
                    fontSize: '0.78rem',
                    fontWeight: 600,
                  }}
                >
                  Fully booked
                </span>
              )}
            </div>
          ),
        },
        {
          key: 'controls',
          display: (
            <div style={{ display: 'flex', gap: 4 }}>
              <IconButton size="small" onClick={() => openEdit(r)}>
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={() => setDeleting(r)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </div>
          ),
        },
      ],
    };
  });

  return (
    <React.Fragment>
      <Paper>
        <Toolbar style={{ justifyContent: 'space-between' }}>
          <Typography variant="subtitle1">
            Promo experiences (e.g. Dumpling Night). Active ones surface on
            the homepage carousel and the dedicated experiences page once
            the website is live.
          </Typography>
          {!panelOpen && (
            <ResponsiveIconButton
              color="primary"
              icon={AddIcon}
              onClick={openCreate}
            >
              Add
            </ResponsiveIconButton>
          )}
        </Toolbar>

        <Collapse in={panelOpen} timeout="auto" unmountOnExit>
          <Paper
            elevation={0}
            style={{ padding: 16, background: '#fafafa', borderTop: '1px solid #eee' }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="subtitle1">
                {form.id ? `Edit experience #${form.id}` : 'New experience'}
              </Typography>
              <IconButton size="small" onClick={() => setPanelOpen(false)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Title"
                  value={form.title}
                  onChange={(e) => {
                    const v = e.target.value;
                    setForm((p) => ({ ...p, title: v }));
                  }}
                  required
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={8}>
                <TextField
                  label="Date / time"
                  type="datetime-local"
                  value={form.experience_at}
                  onChange={(e) => {
                    const v = e.target.value;
                    setForm((p) => ({ ...p, experience_at: v }));
                  }}
                  InputLabelProps={{ shrink: true }}
                  required
                  fullWidth
                />
              </Grid>
              <Grid item xs={6} sm={2}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.is_active}
                      onChange={(_, v) =>
                        setForm((p) => ({ ...p, is_active: v }))
                      }
                    />
                  }
                  label="Active"
                />
              </Grid>
              <Grid item xs={6} sm={2}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.is_fully_booked}
                      onChange={(_, v) =>
                        setForm((p) => ({ ...p, is_fully_booked: v }))
                      }
                    />
                  }
                  label="Fully booked"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Description"
                  value={form.description}
                  onChange={(e) => {
                    const v = e.target.value;
                    setForm((p) => ({ ...p, description: v }));
                  }}
                  multiline
                  rows={3}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="textSecondary">
                  Banner (jpg / png / webp). Optional. Leave blank to keep current.
                </Typography>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const f =
                      e.target.files && e.target.files[0]
                        ? e.target.files[0]
                        : null;
                    setForm((p) => ({ ...p, file: f }));
                  }}
                  style={{ display: 'block', marginTop: 4 }}
                />
                {form.banner_url && !form.file && (
                  <img
                    src={bannerSrc(form.banner_url) || ''}
                    alt=""
                    style={{
                      marginTop: 8,
                      width: 200,
                      height: 100,
                      objectFit: 'cover',
                      borderRadius: 4,
                    }}
                  />
                )}
              </Grid>
              <Grid item xs={12}>
                <Box display="flex" justifyContent="flex-end" style={{ gap: 8 }}>
                  <Button
                    onClick={() => {
                      setPanelOpen(false);
                      setForm(blankForm());
                    }}
                    disabled={submitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSave}
                    disabled={submitting}
                  >
                    Save
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Collapse>

        {loading && <DelayedLinearProgress />}
        {isMobile ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {rows.length === 0 && !loading && (
              <div style={{ padding: 24, textAlign: 'center', color: '#888' }}>
                No experiences yet.
              </div>
            )}
            {rows.map((r) => {
              const src = bannerSrc(r.bannerUrl);
              const dt = new Date(r.experienceAt);
              return (
                <Paper
                  key={r.id}
                  variant="outlined"
                  style={{
                    padding: 12,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                  }}
                >
                  <div style={{ display: 'flex', gap: 12 }}>
                    {src ? (
                      <img
                        src={src}
                        alt=""
                        style={{
                          width: 90,
                          height: 60,
                          objectFit: 'cover',
                          borderRadius: 6,
                          flexShrink: 0,
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: 90,
                          height: 60,
                          background: '#eee',
                          borderRadius: 6,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#aaa',
                          fontSize: 12,
                          flexShrink: 0,
                        }}
                      >
                        no banner
                      </div>
                    )}
                    <div
                      style={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                      }}
                    >
                      <div style={{ fontSize: 15, fontWeight: 700 }}>
                        {r.title}
                      </div>
                      <div style={{ fontSize: 12, color: '#666' }}>
                        {isNaN(dt.getTime()) ? '—' : dt.toLocaleString()}
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          gap: 6,
                          flexWrap: 'wrap',
                          marginTop: 4,
                        }}
                      >
                        <span
                          style={{
                            background: r.isActive ? '#E8F5E9' : '#EEE',
                            color: r.isActive ? '#2E7D32' : '#888',
                            padding: '2px 8px',
                            borderRadius: 4,
                            fontSize: '0.72rem',
                            fontWeight: 600,
                          }}
                        >
                          {r.isActive ? 'Active' : 'Hidden'}
                        </span>
                        {r.isFullyBooked && (
                          <span
                            style={{
                              background: '#FBE4E5',
                              color: '#C5252E',
                              padding: '2px 8px',
                              borderRadius: 4,
                              fontSize: '0.72rem',
                              fontWeight: 600,
                            }}
                          >
                            Fully booked
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<EditIcon />}
                      onClick={() => openEdit(r)}
                      style={{ flex: 1 }}
                    >
                      Edit
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="secondary"
                      startIcon={<DeleteIcon />}
                      onClick={() => setDeleting(r)}
                      style={{ flex: 1 }}
                    >
                      Delete
                    </Button>
                  </div>
                </Paper>
              );
            })}
          </div>
        ) : (
          <SortableTable
            columns={columns}
            rows={tableRows}
            emptyTableText="No experiences yet."
            disablePagination
          />
        )}
      </Paper>

      <ConfirmDialog
        title={`Delete experience "${deleting?.title || ''}"?`}
        dialogProps={{ open: !!deleting, onClose: () => setDeleting(null) }}
        onConfirm={() => {
          if (deleting) handleDelete(deleting);
        }}
      />

      <Snackbar
        open={!!snackbar}
        autoHideDuration={3000}
        onClose={() => setSnackbar(null)}
        message={snackbar}
      />
    </React.Fragment>
  );
};
